import React, { useState, useEffect } from "react";
import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";

import Utils from "../../configs/Utils";
import Enums from "../../configs/Enums";

const LANGUAGES = ["EN", "VI", "DE", "ES", "FR", "IT", "PT", "NL", "JP", "KR"];
const DEFAULT_CONTENT_ITEM = { lang: "EN", subject: "", body: "" };
const INITIAL_FORM_DATA = {
  name: "",
  category: "",
  content: [DEFAULT_CONTENT_ITEM],
};

const CATEGORIES = [
  "Info",
  "Delivered",
  "Tracking",
  "Cancel",
  "Shipping issue",
  "Stock issue",
  "Order Conf.",
  "Payment issue",
  "Return/Refund",
  "Other"
];

const MailTemplateForm = ({
  workspaceId,
  setPopupShow,
  refreshList,
  selectedTemplate,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (selectedTemplate) {
      setFormData({
        name: selectedTemplate.name || "",
        category: selectedTemplate.category || "",
        content:
          selectedTemplate.content?.length > 0
            ? selectedTemplate.content
            : [DEFAULT_CONTENT_ITEM],
      });
      return;
    }

    setFormData(INITIAL_FORM_DATA);
  }, [selectedTemplate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (index, field, value) => {
    const newContent = [...formData.content];
    newContent[index][field] = value;
    setFormData((prev) => ({ ...prev, content: newContent }));
  };

  const addContentItem = () => {
    setFormData((prev) => ({
      ...prev,
      content: [...prev.content, { ...DEFAULT_CONTENT_ITEM }],
    }));
  };

  const removeContentItem = (index) => {
    if (formData.content.length === 1) {
      toast.warning("You must have at least one content language.");
      return;
    }
    const newContent = [...formData.content];
    newContent.splice(index, 1);
    setFormData((prev) => ({ ...prev, content: newContent }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Process payload
      const payload = {
        ...formData,
        workspace: workspaceId,
      };

      if (selectedTemplate) {
        // Update
        await Utils._apiRequest({
          path: `${Enums.endpoints.mailtemplate.ALL}/${selectedTemplate._id}`,
          type: "PUT",
          payload,
        });
        toast.success("Mail template updated successfully");
      } else {
        // Create
        await Utils._apiRequest({
          path: Enums.endpoints.mailtemplate.ALL,
          type: "POST",
          payload,
        });
        toast.success("Mail template created successfully");
      }

      refreshList();
      setPopupShow(false);
    } catch (error) {
      toast.error(error.message || "Failed to save mail template");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="mail-template-form">
      <section className="mail-template-form__intro">
        <span className="mail-template-form__eyebrow">
          {selectedTemplate ? "Template editor" : "Template creation"}
        </span>
        <h3>{selectedTemplate ? "Refine your email template" : "Create a new email template"}</h3>
        <p>
          Configure the template name, category, and language-specific content for this workspace.
        </p>
      </section>

      <section className="mail-template-form__section mail-template-form__meta">
        <Row className="g-3">
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Name *</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g. Order Confirmation"
              required
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>Category</Form.Label>
            <Form.Select
              name="category"
              value={formData.category}
              onChange={handleChange}
            >
              <option value="">Select a category</option>
              {CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        </Row>
      </section>

      <div className="mail-template-form__toolbar d-flex justify-content-between align-items-center mb-3 mt-4">
        <div>
          <h6 className="mb-1 fw-bold">Localized content blocks</h6>
          <p className="mail-template-form__toolbar-copy mb-0">
            Define one or more language variants with localized subject and body.
          </p>
        </div>
        <Button variant="outline-primary" size="sm" onClick={addContentItem} type="button">
          <FontAwesomeIcon icon={faPlus} className="me-2" /> Add Language
        </Button>
      </div>

      {formData.content.map((item, index) => (
        <Card className="mb-3 shadow-sm mail-template-language-card" key={index}>
          <Card.Body>
            <div className="mail-template-language-card__header">
              <div>
                <span className="mail-template-language-card__label">Locale {index + 1}</span>
                <h6 className="mb-0">Template localization</h6>
              </div>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeContentItem(index)}
                title="Remove Language"
                type="button"
              >
                <FontAwesomeIcon icon={faTrash} className="me-2" />
                Remove
              </Button>
            </div>

            <Row className="g-3">
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Language *</Form.Label>
                  <Form.Select
                    value={item.lang}
                    onChange={(e) =>
                      handleContentChange(index, "lang", e.target.value)
                    }
                    required
                  >
                    {LANGUAGES.map((l) => (
                      <option key={l} value={l}>
                        {l}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={9}>
                <Form.Group className="mb-3">
                  <Form.Label>Subject *</Form.Label>
                  <Form.Control
                    type="text"
                    value={item.subject}
                    onChange={(e) =>
                      handleContentChange(index, "subject", e.target.value)
                    }
                    required
                    placeholder="Email Subject"
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-0">
                  <Form.Label>Body *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={item.body}
                    onChange={(e) =>
                      handleContentChange(index, "body", e.target.value)
                    }
                    required
                    placeholder="Email Body Content (HTML allowed)"
                    className="mail-template-form__textarea"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      ))}

      <div className="d-grid gap-2 d-md-flex justify-content-md-end mt-4 mail-template-form__actions">
        <Button
          variant="secondary"
          className="me-md-2"
          onClick={() => setPopupShow(false)}
          disabled={submitting}
          type="button"
        >
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={submitting}>
          {submitting ? "Saving..." : "Save Template"}
        </Button>
      </div>
    </Form>
  );
};

export default MailTemplateForm;
